import React, {
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import EditIcon from '@images/edit.svg';

import { Stack } from '@mui/material';

import {
  Button,
  Form,
  FormField, IconButton,
  PhoneField,
  TextField,
  Typography,
} from '@elements';
import { changeContactsDataSchema } from './changeContacts.schema';
import { ChangeContactsValues } from './changeContacts.types';
import { ProfileContext } from '@contexts';
import useUpdateContactData from '@hooks/useUpdateContactData';
import { AxiosError } from 'axios';
import ConfirmForm from './ConfirmForm';
import { ApiErrorsTypes, ApiResponse, AxiosErrorResponseData } from '@enums';
import SearchAddressInput from '@elements/form/fields/search-address-input';
import { AddressDetailsBox } from './changeContacts.form.styles';
import { StyledIcon } from '@elements/icon/icon.styles';
import { getFullAddress } from '@helpers';

type Props = {
  handleClose: () => void;
};

const ChangeContactsForm = ({ handleClose }: Props) => {
  const data = useContext(ProfileContext);
  const { t } = useTranslation();
  const { mutate, isPending, error, isSuccess } = useUpdateContactData();
  const [showConformationForm, setShowConformationForm] = useState(false);
  const [editableAddressDetails, setEditableAddressDetails] = useState(false);
  const [verificationIdentifier, setVerificationIdentifier] = useState<
    string | undefined
  >();
  useEffect(() => {
    if (error) {
      const axiosError = error as AxiosError<AxiosErrorResponseData>;
      if (
        axiosError.response?.status === ApiResponse.Unprocessable &&
        axiosError.response?.data?.type === ApiErrorsTypes.verificationRequired
      ) {
        setVerificationIdentifier(
          axiosError.response?.data?.verificationIdentifier,
        );
        setShowConformationForm(true);
      }
    }
  }, [error]);

  useEffect(() => {
    if (isSuccess) {
      handleClose();
    }
  }, [isSuccess, handleClose]);

  const initialValues = useMemo(
    () => ({
      phone: data?.phone ?? '',
      email: data?.email ?? '',
      address: data?.address ? getFullAddress(data.address) : '',
      postalCode: data?.address?.postalCode ?? '',
      regionName: data?.address?.regionName ?? '',
      cityName: data?.address?.cityName ?? '',
      streetName: data?.address?.streetName ?? '',
      houseNumber: data?.address?.houseNumber ?? '',
      flatNumber: data?.address?.flatNumber ?? '',
    }),
    [data],
  );

  const onFormSubmit = (values: Record<string, unknown>): void => {
    const { phone, email, postalCode, regionName, cityName, streetName, houseNumber, flatNumber } = values as unknown as ChangeContactsValues;
    mutate({
      phone,
      email,
      address: {
        postalCode,
        regionName,
        cityName,
        streetName,
        houseNumber,
        flatNumber,
      },
    });
  };

  if (showConformationForm) {
    return (
      <ConfirmForm
        handleClose={handleClose}
        email={data?.email}
        verificationIdentifier={verificationIdentifier ?? ''}
      />
    );
  }

  return (
    <Stack direction="column" gap={3} pt={3}>
      <Form
        validateSchema={changeContactsDataSchema()}
        onSubmit={onFormSubmit}
        initialValues={initialValues}
      >
        {({
          invalid,
          submitting,
          pristine,
          handleSubmit,
          values,
          form,
        }: FormRenderProps<ChangeContactsValues>): JSX.Element => {
          const isDirty = form.getState().dirty;
          const isOnlyAddressChanged =
            values.address !== initialValues.address &&
            values.phone === initialValues.phone &&
            values.email === initialValues.email;

          const buttonText =
            !isDirty || isOnlyAddressChanged
              ? t('forms.changePersonalData.submit')
              : t('forms.contacts.continue');

          return (
            <>
              <Typography align="center" color="white" fontSize={22}>
                {t('forms.contacts.title')}
              </Typography>
              <Stack direction="column" spacing={3}>
                <FormField
                  name="phone"
                  component={PhoneField}
                  fullWidth
                  size="large"
                  label={t('forms.contacts.phone').toString()}
                />
                <FormField
                  name="email"
                  component={TextField}
                  fullWidth
                  size="large"
                  type="text"
                  label={t('forms.contacts.email').toString()}
                />
                <FormField
                  name="address"
                  component={SearchAddressInput}
                  fullWidth
                  label={t('forms.contacts.address').toString()}
                  onSelect={(item: any): void => {
                    if(item?.data){
                      form.batch(() => {
                        form.change('postalCode', item?.data?.postal_code ?? '');
                        form.change('regionName', item?.data?.region ?? '');
                        form.change('cityName', item?.data?.city ?? '');
                        form.change('streetName', item?.data?.street_with_type ?? '');
                        form.change('houseNumber', ((item?.data?.house_type ?? '') + (item?.data?.house ?? '') + (item?.data?.block_type ?? '') + (item?.data?.block ?? '')));
                        form.change('flatNumber', ((item?.data?.flat_type ?? '') + (item?.data?.flat ?? '')));
                      });
                    }
                  }}
                />
                <AddressDetailsBox>
                  <Stack direction='row' gap={2} justifyContent='center' alignItems='center'>
                    <Typography color='white' variant='h5' fontWeight={500}>
                      {t('forms.contacts.addressDetails').toString()}
                    </Typography>
                    <IconButton
                      aria-label="change contact data"
                      edge="start"
                      sx={{padding: 0}}
                      onClick={() => setEditableAddressDetails(val => !val)}
                    >
                      <StyledIcon src={EditIcon} width={18} height={18} />
                    </IconButton>
                  </Stack>
                  <Stack direction='column' gap={1} pt={2}>
                    <FormField
                      name="postalCode"
                      component={TextField}
                      fullWidth
                      size="large"
                      type="text"
                      label={t('forms.contacts.postalCode').toString()}
                      disabled={!editableAddressDetails}
                      sx={{
                        '.MuiInputBase-input.Mui-disabled': {
                          color: 'rgb(124, 122, 129)',
                          '-webkit-text-fill-color': 'rgb(124, 122, 129)'
                        }
                      }}
                      maskProps={{
                        mask:      String,
                        lazy:      true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        maxLength: 150,
                      }}
                    />
                    <FormField
                      name="regionName"
                      component={TextField}
                      fullWidth
                      size="large"
                      type="text"
                      label={t('forms.contacts.regionName').toString()}
                      disabled={!editableAddressDetails}
                      sx={{
                        '.MuiInputBase-input.Mui-disabled': {
                          color: 'rgb(124, 122, 129)',
                          '-webkit-text-fill-color': 'rgb(124, 122, 129)'
                        }
                      }}
                      maskProps={{
                        mask:      String,
                        lazy:      true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        maxLength: 150,
                      }}
                    />
                    <FormField
                      name="cityName"
                      component={TextField}
                      fullWidth
                      size="large"
                      type="text"
                      label={t('forms.contacts.cityName').toString()}
                      disabled={!editableAddressDetails}
                      sx={{
                        '.MuiInputBase-input.Mui-disabled': {
                          color: 'rgb(124, 122, 129)',
                          '-webkit-text-fill-color': 'rgb(124, 122, 129)'
                        }
                      }}
                      maskProps={{
                        mask:      String,
                        lazy:      true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        maxLength: 150,
                      }}
                    />
                    <FormField
                      name="streetName"
                      component={TextField}
                      fullWidth
                      size="large"
                      type="text"
                      label={t('forms.contacts.streetName').toString()}
                      disabled={!editableAddressDetails}
                      sx={{
                        '.MuiInputBase-input.Mui-disabled': {
                          color: 'rgb(124, 122, 129)',
                          '-webkit-text-fill-color': 'rgb(124, 122, 129)'
                        }
                      }}
                      maskProps={{
                        mask:      String,
                        lazy:      true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        maxLength: 150,
                      }}
                    />
                    <FormField
                      name="houseNumber"
                      component={TextField}
                      fullWidth
                      size="large"
                      type="text"
                      label={t('forms.contacts.houseNumber').toString()}
                      disabled={!editableAddressDetails}
                      sx={{
                        '.MuiInputBase-input.Mui-disabled': {
                          color: 'rgb(124, 122, 129)',
                          '-webkit-text-fill-color': 'rgb(124, 122, 129)'
                        }
                      }}
                      maskProps={{
                        mask:      String,
                        lazy:      true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        maxLength: 150,
                      }}
                    />
                    <FormField
                      name="flatNumber"
                      component={TextField}
                      fullWidth
                      size="large"
                      type="text"
                      label={t('forms.contacts.flatNumber').toString()}
                      disabled={!editableAddressDetails}
                      sx={{
                        '.MuiInputBase-input.Mui-disabled': {
                          color: 'rgb(124, 122, 129)',
                          '-webkit-text-fill-color': 'rgb(124, 122, 129)'
                        }
                      }}
                      maskProps={{
                        mask:      String,
                        lazy:      true,
                        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
                        // @ts-ignore
                        maxLength: 150,
                      }}
                    />
                  </Stack>
                </AddressDetailsBox>
                <Button
                  variant="contained"
                  color="internationalOrange"
                  disabled={invalid || submitting || pristine || isPending}
                  fullWidth
                  size="large"
                  onClick={handleSubmit}
                  type="submit"
                  sx={{
                    textTransform: 'uppercase',
                    marginTop: '158px !important',
                  }}
                >
                  {buttonText}
                </Button>
              </Stack>
            </>
          );
        }}
      </Form>
    </Stack>
  );
};

export default ChangeContactsForm;
